import React, { useCallback, useMemo, useRef } from 'react';

import { noop } from 'lodash-es';
import { useIntl } from 'react-intl';
import { Pressable } from 'react-native';
import styled from 'styled-components/native';

import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import { useFeatureNavigation } from 'hooks/use-feature-navigation';
import { useOrderHref } from 'hooks/use-order-href';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag, useLDContext } from 'state/launchdarkly';
import { useLocationContext, useMatch } from 'state/location';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { useOffersContext } from 'state/offers';
import { useOrderContext } from 'state/order';
import { useScrollContext } from 'state/scroll';
import { useStoreContext } from 'state/store';
import { isRecentItemsRoute, routes } from 'utils/routing';

import CheckoutButtonLink from './checkout-button-link';
import { MobileWebNavigationBarSkeleton } from './mobile-web-navigation-bar.skeleton';
import { MobileWebNavigationBorder } from './mobile-web-navigation-border';
import { getNavLinks } from './nav-links';
import { NavigationMobileTab } from './types';
import { generateNavigationMobileTabs } from './utils-sanity';

// The types for react native do not support `dataSet` yet, but
// it is the required way to pass data-* attributes to the node.
// And we are using this mechanism to ensure these nodes are rendered
// during the pre-render, but only visible for the correct screen size.
const onlyRenderInMobile = () =>
  ({
    dataSet: { mediaquery: 'mobile' },
  } as any);

const MobileNavInner = styled.View`
  position: relative;
`;

const MobileWebNavigationContainer = styled.View`
  justify-content: space-around;
  flex-direction: row;
  background-color: ${p => p.theme.token('bottom-nav-bar-background-color')};
`;

interface IMobileCheckoutButtonProps {
  checkoutRoute: string;
  enableBottomService: boolean;
}

const MobileCheckoutButton: React.FC<IMobileCheckoutButtonProps> = ({
  checkoutRoute,
  enableBottomService,
}) => {
  const baseRouteMatch = useMatch(`${routes.base}*`);
  const storeLocatorMatch = useMatch(`${routes.storeLocator}/*`);
  const menuMatch = useMatch(`${routes.menu}/*`);
  const cartMatch = useMatch(`${routes.cart}/*`);
  const orderConfirmationMatch = useMatch(`${routes.orderConfirmation}/*`);
  const order: any = useOrderContext();
  const offers = useOffersContext();
  const { isAuthenticated } = useAuthContext();
  const cartButton = useRef<HTMLAnchorElement>(null);
  const { logEvent } = useMParticleContext();
  const logMobileCheckoutEvent = useCallback(() => {
    logEvent(CustomEventNames.CHECKOUT_MOBILE, EventTypes.Navigation);
  }, [logEvent]);
  // Do not show checkout button when bottom service mode is enabled
  if (enableBottomService) {
    return null;
  }
  if (!baseRouteMatch) {
    return null;
  }
  // We don't want to show the checkout cart on the store-locator page
  if (storeLocatorMatch) {
    return null;
  }

  if (menuMatch) {
    const menuObjectId = menuMatch.params['*'];
    if (menuObjectId && !menuObjectId!.startsWith('section')) {
      return null;
    }
  }

  if (
    ((order.cartEntries.length || offers.selectedOfferCartEntry) && !cartMatch) ||
    orderConfirmationMatch
  ) {
    return (
      <CheckoutButtonLink
        amount={order.calculateCartTotal()}
        to={checkoutRoute}
        ref={cartButton}
        logMobileCheckoutEvent={isAuthenticated() ? logMobileCheckoutEvent : noop}
      />
    );
  }
  return null;
};

const MobileWebNavigationBar: React.FC = () => {
  const menuHref = useOrderHref();
  const order: any = useOrderContext();
  const { isStoreOpenAndAvailable } = useStoreContext();
  const enableOffers = useFlag(LaunchDarklyFlag.ENABLE_OFFERS);
  const enableBottomService = useFlag(LaunchDarklyFlag.ENABLE_BOTTOM_SERVICE_MODE);
  const enableSanityControlledMobileNav = useFlag(
    LaunchDarklyFlag.ENABLE_SANITY_CONTROLLED_MOBILE_NAV
  );
  const enableStaticMenu = useFlag(LaunchDarklyFlag.ENABLE_STATIC_MENU);
  const enableAccountInFooter = useFlag(LaunchDarklyFlag.ENABLE_ACCOUNT_IN_FOOTER);
  const navbarWithServiceModeSelected = useFlag(LaunchDarklyFlag.NAVBAR_WITH_SERVICE_MODE_SELECTED);

  const {
    featureNavigationMobile,
    featureNavigatonMobileWithStore,
    featureNavigationLoading,
  } = useFeatureNavigation();
  const { formatMessage } = useIntl();
  const { location } = useLocationContext();
  const { logEvent } = useMParticleContext();
  const { store } = useStoreContext();

  const cartButton = useRef<HTMLAnchorElement>(null);
  const { flags: LDFlags } = useLDContext();

  const { scrollTo } = useScrollContext();

  const signupMatch = useMatch(routes.signUp);
  const signinMatch = useMatch(routes.signIn);
  const optMatch = useMatch(routes.confirmOtp);

  const logMobileNavOrderEvent = useCallback(() => {
    logEvent(CustomEventNames.MOBILE_NAV_ORDER, EventTypes.Navigation);
  }, [logEvent]);
  const navLinks: NavigationMobileTab[] = useMemo(() => {
    if (enableSanityControlledMobileNav && featureNavigationLoading) {
      return [];
    }

    const sanityNavEnabled = enableSanityControlledMobileNav && featureNavigationMobile;

    // The Navigation Tabs With Service Mode / Store Selected should only be used when flag is enabled
    // and items are configured in Sanity. Otherwise we should display default menu items
    const shouldDisplayServiceModeMenu =
      navbarWithServiceModeSelected && store._id && featureNavigatonMobileWithStore.length;

    const links = shouldDisplayServiceModeMenu
      ? featureNavigatonMobileWithStore
      : featureNavigationMobile;

    const isLocationInRecentItemsRoute = isRecentItemsRoute(location.pathname);

    return sanityNavEnabled
      ? generateNavigationMobileTabs(links, LDFlags, isLocationInRecentItemsRoute)
      : getNavLinks({
          menuHref,
          formatMessage,
          enableOffers,
          enableStaticMenu,
          enableAccountInFooter,
          logMobileNavOrderEvent,
        });
  }, [
    enableSanityControlledMobileNav,
    featureNavigationLoading,
    featureNavigationMobile,
    navbarWithServiceModeSelected,
    store._id,
    featureNavigatonMobileWithStore,
    location.pathname,
    LDFlags,
    menuHref,
    formatMessage,
    enableOffers,
    enableStaticMenu,
    enableAccountInFooter,
    logMobileNavOrderEvent,
  ]);

  useEffectOnUpdates(() => {
    window.setTimeout(() => {
      cartButton.current?.focus();
    }, 100);
  }, [order.cartEntries.length]);

  const onClickNavLink = useCallback(
    (href: string) => {
      if (location.pathname === href) {
        // whenever a mobile nav button is pressed when the route already matches,
        // scroll to the top of the view
        scrollTo({ animated: false });
      }
    },
    [location.pathname, scrollTo]
  );

  const { serviceMode } = order;

  // TODO: @glengal checkoutRoute is not used. Expected? this is potentially a bug
  let checkoutRoute = routes.cart;
  if (!serviceMode) {
    checkoutRoute = routes.serviceMode;
  } else if (!isStoreOpenAndAvailable) {
    checkoutRoute = routes.storeLocator;
  }

  // not showing mobile navigation bar on sign up flow
  if (signupMatch || signinMatch || optMatch) {
    return null;
  }

  return (
    <MobileWebNavigationBorder {...onlyRenderInMobile()}>
      <MobileNavInner>
        <MobileWebNavigationContainer>
          {navLinks?.length ? (
            navLinks.map((item, index) => (
              <Pressable
                key={index}
                onPress={() => item.selectedHrefs[0] && onClickNavLink(item.selectedHrefs[0])}
              >
                <item.Component />
              </Pressable>
            ))
          ) : (
            <MobileWebNavigationBarSkeleton />
          )}
        </MobileWebNavigationContainer>
        {isStoreOpenAndAvailable && (
          <MobileCheckoutButton
            checkoutRoute={checkoutRoute}
            enableBottomService={enableBottomService}
          />
        )}
      </MobileNavInner>
    </MobileWebNavigationBorder>
  );
};

export default MobileWebNavigationBar;
